/**
 * Connectivity tracking shared by the React layer and the raw API client.
 *
 * `navigator.onLine` on its own is not trustworthy: it reports the presence of
 * a network interface, not of the internet. A phone sitting on a captive-portal
 * wifi, or a laptop whose VPN just dropped, both report `onLine === true` while
 * every request fails. So the browser signal is treated as a fast *hint* and
 * confirmed against a real request before the UI tells the user they're offline.
 *
 * The negative direction is reliable, though — no interface at all means nothing
 * can succeed — so `onLine === false` flips us offline immediately, with no probe.
 *
 * This module is deliberately framework-free so `lib/api.ts` can report
 * transport failures without reaching into React. `hooks/useOnlineStatus.ts`
 * is the React binding.
 */

import { resolveApiBase } from './mountBase'

export type ConnectivityStatus = 'online' | 'offline'

export interface ConnectivitySnapshot {
  status: ConnectivityStatus
  /** Epoch ms of the last status change (0 until the first transition). */
  since: number
  /** True while a probe request is in flight. */
  checking: boolean
}

/** How long a probe may hang before we call it a failure. */
export const PROBE_TIMEOUT_MS = 6000
/** First re-check delay after going offline. */
export const RECHECK_BASE_MS = 2000
/**
 * Ceiling for the offline re-check backoff.
 *
 * Deliberately short. The browser's `online` event is the fast path, but it is
 * not guaranteed — it doesn't fire for captive portals, and mobile browsers
 * miss it often enough that the polling loop is what actually recovers most
 * sessions. A probe is one tiny request, so being stale for half a minute costs
 * the user far more than checking every fifteen seconds costs the network.
 */
export const RECHECK_MAX_MS = 15000

const listeners = new Set<() => void>()

let snapshot: ConnectivitySnapshot = {
  status: initialStatus(),
  since: 0,
  checking: false,
}

let probeInFlight: Promise<boolean> | null = null
let recheckTimer: ReturnType<typeof setTimeout> | null = null
let recheckDelay = RECHECK_BASE_MS
let monitoring = false
let teardownMonitor: (() => void) | null = null
let probeCounter = 0

function initialStatus(): ConnectivityStatus {
  if (typeof navigator === 'undefined') return 'online'
  return navigator.onLine === false ? 'offline' : 'online'
}

function emit() {
  listeners.forEach((listener) => listener())
}

function patchSnapshot(next: Partial<ConnectivitySnapshot>) {
  const status = next.status ?? snapshot.status
  const checking = next.checking ?? snapshot.checking
  if (status === snapshot.status && checking === snapshot.checking) return

  snapshot = {
    status,
    checking,
    since: status === snapshot.status ? snapshot.since : Date.now(),
  }
  emit()
}

// -----------------------------------------------------------------------------
// Store surface (feeds useSyncExternalStore)
// -----------------------------------------------------------------------------

export function subscribeConnectivity(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function getConnectivitySnapshot(): ConnectivitySnapshot {
  return snapshot
}

/** SSR/prerender never has a network story to tell — assume online. */
const SERVER_SNAPSHOT: ConnectivitySnapshot = { status: 'online', since: 0, checking: false }
export function getServerConnectivitySnapshot(): ConnectivitySnapshot {
  return SERVER_SNAPSHOT
}

export function isOffline(): boolean {
  return snapshot.status === 'offline'
}

// -----------------------------------------------------------------------------
// Probing
// -----------------------------------------------------------------------------

function probeUrl(): string {
  // The service worker deliberately skips `api/` paths, so this always reaches
  // the network instead of being answered from cache — which is the entire
  // point of a connectivity probe. The counter defeats HTTP caches too.
  probeCounter += 1
  return `${resolveApiBase()}/health?_probe=${Date.now()}-${probeCounter}`
}

/**
 * Ask the network a question and see whether anything answers.
 *
 * ANY HTTP response counts as online — including 401, 403 and 5xx. A 500 means
 * the server is unwell, not that the user has no internet, and telling someone
 * to "check your connection" when their wifi is fine is worse than useless.
 * Only a transport-level throw (DNS failure, no route, TLS failure, timeout)
 * means there is no connectivity.
 */
async function runProbe(): Promise<boolean> {
  if (typeof fetch !== 'function') return true

  const controller = typeof AbortController === 'function' ? new AbortController() : null
  const timer = controller
    ? setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS)
    : null

  try {
    await fetch(probeUrl(), {
      method: 'GET',
      cache: 'no-store',
      credentials: 'include',
      signal: controller?.signal,
    })
    return true
  } catch {
    return false
  } finally {
    if (timer !== null) clearTimeout(timer)
  }
}

/**
 * Run a probe now and fold the result into the snapshot. Concurrent callers
 * share the in-flight probe rather than stacking requests on a network that is,
 * by hypothesis, already struggling.
 */
export function checkConnectivityNow(): Promise<boolean> {
  if (probeInFlight) return probeInFlight

  // No interface at all: skip the round-trip, we already know the answer.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    goOffline()
    return Promise.resolve(false)
  }

  patchSnapshot({ checking: true })

  probeInFlight = runProbe()
    .then((reachable) => {
      probeInFlight = null
      if (reachable) {
        goOnline()
      } else {
        goOffline()
      }
      patchSnapshot({ checking: false })
      return reachable
    })
    .catch(() => {
      probeInFlight = null
      patchSnapshot({ checking: false })
      return false
    })

  return probeInFlight
}

function goOnline() {
  clearRecheck()
  recheckDelay = RECHECK_BASE_MS
  patchSnapshot({ status: 'online' })
}

function goOffline() {
  patchSnapshot({ status: 'offline' })
  scheduleRecheck()
}

function clearRecheck() {
  if (recheckTimer !== null) {
    clearTimeout(recheckTimer)
    recheckTimer = null
  }
}

/**
 * While offline, keep asking — with backoff — so the app heals itself the
 * moment signal returns instead of waiting for the user to poke it. Browsers
 * do fire an `online` event, but it is unreliable on mobile (and never fires
 * at all when the failure was a captive portal rather than a dropped link).
 */
function scheduleRecheck() {
  if (recheckTimer !== null || !monitoring) return

  recheckTimer = setTimeout(() => {
    recheckTimer = null
    void checkConnectivityNow().then((reachable) => {
      if (!reachable) {
        recheckDelay = Math.min(recheckDelay * 2, RECHECK_MAX_MS)
        scheduleRecheck()
      }
    })
  }, recheckDelay)
}

// -----------------------------------------------------------------------------
// Reports from the API client
// -----------------------------------------------------------------------------

/**
 * Called when a request throws at the transport layer. One failure is not proof
 * of an outage — a single request can die for its own reasons — so we confirm
 * with a probe before flipping the UI, unless the browser already told us the
 * interface is down.
 */
export function reportTransportFailure(): void {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    goOffline()
    return
  }
  if (snapshot.status === 'offline') {
    // Already offline and already re-checking; nothing to add.
    return
  }
  void checkConnectivityNow()
}

/**
 * Called when a request completes with an HTTP response. This is the strongest
 * evidence available that the network works, so it heals the offline state
 * immediately without waiting for the backoff timer.
 */
export function reportTransportSuccess(): void {
  if (snapshot.status === 'online') return
  goOnline()
}

// -----------------------------------------------------------------------------
// Monitor lifecycle
// -----------------------------------------------------------------------------

/**
 * Wire up browser connectivity events. Idempotent — the first caller starts the
 * monitor and every caller gets a no-op teardown, because connectivity is a
 * process-wide concern that outlives any single component.
 */
export function startConnectivityMonitor(): () => void {
  if (monitoring) return () => {}
  if (typeof window === 'undefined') return () => {}

  monitoring = true

  const handleOffline = () => {
    goOffline()
  }

  const handleOnline = () => {
    // The `online` event only means an interface came back. Confirm that it
    // actually carries traffic before clearing the banner — otherwise joining
    // a captive-portal wifi silently reports success.
    void checkConnectivityNow()
  }

  // Coming back to the app is a strong signal the user wants it working now.
  // Phones also suspend timers in the background, so a PWA returning to the
  // foreground can be sitting on a stale offline state with no pending
  // re-check. Reset the backoff and probe immediately on the way in.
  const probeOnReturn = () => {
    if (snapshot.status !== 'offline') return
    recheckDelay = RECHECK_BASE_MS
    void checkConnectivityNow()
  }

  const handleVisibility = () => {
    if (document.visibilityState === 'visible') probeOnReturn()
  }

  window.addEventListener('offline', handleOffline)
  window.addEventListener('online', handleOnline)
  window.addEventListener('focus', probeOnReturn)
  document.addEventListener('visibilitychange', handleVisibility)

  if (snapshot.status === 'offline') scheduleRecheck()

  teardownMonitor = () => {
    window.removeEventListener('offline', handleOffline)
    window.removeEventListener('online', handleOnline)
    window.removeEventListener('focus', probeOnReturn)
    document.removeEventListener('visibilitychange', handleVisibility)
    clearRecheck()
    monitoring = false
    teardownMonitor = null
  }

  return () => {}
}

/** Test-only: tear the module back down to a fresh state. */
export function __resetConnectivityForTests(): void {
  teardownMonitor?.()
  clearRecheck()
  listeners.clear()
  probeInFlight = null
  recheckDelay = RECHECK_BASE_MS
  monitoring = false
  probeCounter = 0
  snapshot = { status: initialStatus(), since: 0, checking: false }
}
